import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { SearchField, Pager, paginate } from './ListControls';
import PageHeader from './PageHeader';
import { T, FONT_MONO } from '../constants/tokens';
import { setAlert } from '../redux/actions/alertActions';

const monoTag = {
  background: T.hairlineSoft,
  borderRadius: 9999,
  padding: '4px 12px',
  fontFamily: FONT_MONO,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  textTransform: 'uppercase',
  color: T.body
};

const button = (tone) => ({
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid ' + T.hairline,
  background: T.elevated,
  color: tone === 'danger' ? '#ee0000' : T.ink,
  cursor: 'pointer',
  whiteSpace: 'nowrap'
});

export const primaryButton = {
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  height: 40,
  padding: '0 20px',
  borderRadius: 6,
  border: 0,
  background: '#f7a501',
  color: '#23251d',
  cursor: 'pointer',
  whiteSpace: 'nowrap'
};

/**
 * The design's configuration table: a card with the title, a record count,
 * search and "Add {record}", then a grid of rows each ending in its actions.
 *
 * Outside setup mode the table is read-only, as the design has it: no add
 * button, and "Locked" where the actions would be. A table that is not
 * setup-gated (users) passes `alwaysEditable`.
 *
 * Rows come from `load`; call `reload()` (via a ref) after a change.
 */
class AdminTable extends Component {
  constructor(props) {
    super(props);
    this.state = { rows: null, search: '', page: 1 };
    this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;
    this.reload();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  reload = () => {
    return this.props.load()
      .then((rows) => {
        if (this.mounted) this.setState({ rows: rows ?? [] });
      })
      .catch((error) => {
        if (this.mounted) this.setState({ rows: [] });
        this.props.setError(error ? error : 'Unable to retrieve ' + this.props.title.toLowerCase() + '.');
      });
  }

  render() {
    const { title, singular, columns, rowActions, onAdd, addLabel, idOf, searchText, searchPlaceholder, setupMode, alwaysEditable } = this.props;
    const editable = alwaysEditable || setupMode;
    const all = this.state.rows ?? [];

    const query = this.state.search.trim().toLowerCase();
    const shown = query === '' ? all : all.filter((row) => searchText(row).toLowerCase().includes(query));
    const { page, pageCount, pageRows } = paginate(shown, this.state.page);

    const grid = columns.map((c) => c.width).join(' ') + ' minmax(150px, auto)';
    const minWidth = columns.reduce((n, c) => n + (c.minWidth ?? 120), 150) + columns.length * 16 + 48;

    return (
      <>
        {/* The design titles the page with the table's name, then repeats it on the card. */}
        <PageHeader title={title} />
        <section style={{ background: T.elevated, border: '1px solid ' + T.hairline, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: 24, borderBottom: '1px solid ' + T.hairlineSoft }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, lineHeight: '28px', letterSpacing: '-0.4px', color: T.ink }}>{title}</h2>
            <span style={{ background: T.hairlineSoft, borderRadius: 9999, padding: '4px 12px', fontSize: 12, fontWeight: 500, lineHeight: '16px', color: T.ink }}>
              {this.state.rows == null ? 'Loading…' : (all.length === 1 ? '1 record' : all.length + ' records')}
            </span>
            <div style={{ flex: '1 0 12px' }} />
            <SearchField
              value={this.state.search}
              onChange={(e) => this.setState({ search: e.target.value, page: 1 })}
              placeholder={searchPlaceholder ?? 'Search ' + title.toLowerCase()}
              label={'Search ' + title.toLowerCase()}
              // Wraps below the title rather than shrinking out of use.
              style={{ flex: '0 1 260px', minWidth: 200 }}
            />
            {onAdd && editable &&
            <button type='button' style={primaryButton} onClick={onAdd}>{addLabel ?? 'Add ' + singular}</button>}
            {!editable &&
            <span style={monoTag}>Read-only · enable setup mode to edit</span>}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth }}>
              <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 16, padding: '12px 24px', background: T.surfaceSoft, borderBottom: '1px solid ' + T.hairline }}>
                {columns.concat({ label: 'Actions' }).map((c, i) => (
                  <span
                    key={c.label}
                    style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, lineHeight: '16px', textTransform: 'uppercase', color: T.body, textAlign: i === columns.length ? 'right' : 'left' }}
                  >
                    {c.label}
                  </span>
                ))}
              </div>

              {pageRows.map((row) => (
                <div
                  key={idOf(row)}
                  style={{ display: 'grid', gridTemplateColumns: grid, gap: 16, alignItems: 'center', padding: '0 24px', borderBottom: '1px solid ' + T.hairlineSoft, minHeight: 56 }}
                >
                  {columns.map((c, i) => {
                    const value = c.render(row);
                    return (
                      <span
                        key={c.label}
                        title={typeof value === 'string' ? value : undefined}
                        style={{
                          fontFamily: i === 0 ? FONT_MONO : 'inherit',
                          fontSize: 14,
                          fontWeight: i === 0 ? 500 : 400,
                          lineHeight: 1.5,
                          color: i === 0 ? T.ink : T.body,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0
                        }}
                      >
                        {value == null || value === '' ? '—' : value}
                      </span>
                    );
                  })}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '8px 0' }}>
                    {editable
                      ? rowActions(row).map((a) => (
                        <button key={a.label} type='button' style={button(a.tone)} onClick={() => a.onClick(row)}>{a.label}</button>
                      ))
                      : <span style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, lineHeight: '16px', textTransform: 'uppercase', color: T.body }}>Locked</span>}
                  </div>
                </div>
              ))}

              {this.state.rows != null && shown.length === 0 &&
              <div style={{ padding: 24, fontSize: 14, color: T.muted }}>
                {all.length === 0 ? 'No ' + title.toLowerCase() + ' yet.' : 'Nothing matches “' + this.state.search.trim() + '”.'}
              </div>}
            </div>
          </div>

          <div style={{ padding: pageCount > 1 ? '0 24px 16px' : 0 }}>
            <Pager page={page} pageCount={pageCount} onChange={(p) => this.setState({ page: p })} />
          </div>
        </section>
      </>
    );
  }
}

AdminTable.propTypes = {
  title: PropTypes.string.isRequired,
  singular: PropTypes.string.isRequired,
  /** { label, width (a grid track), minWidth (px, for horizontal scroll), render(row) } */
  columns: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    width: PropTypes.string.isRequired,
    minWidth: PropTypes.number,
    render: PropTypes.func.isRequired
  })).isRequired,
  load: PropTypes.func.isRequired,
  idOf: PropTypes.func.isRequired,
  searchText: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string,
  /** The buttons at the end of a row: [{ label, onClick(row), tone: 'danger'? }] */
  rowActions: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
  addLabel: PropTypes.string,
  alwaysEditable: PropTypes.bool,
  setupMode: PropTypes.bool.isRequired,
  setError: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  setupMode: state.setupMode.mode === true
});

const mapDispatchToProps = (dispatch) => ({
  setError: (msg) => setAlert(dispatch, msg)
});

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(AdminTable);
